import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import { portalPaths } from '@provision/portal-routes';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DISPLAY_DATE_TIME_FORMAT } from 'src/constants/date';
import { useGetPortalioAuthDetails } from 'src/graphql/entities/integrations/portalio/hooks/use-get-portalio-auth-details';
import {
  PortalioProposalItem,
  useGetPortalioProposals,
} from 'src/graphql/entities/integrations/portalio/hooks/use-get-portalio-proposals';
import { useNavigateRoute } from 'src/utils/routing';
import { ProjectsListProps } from './import-proposals.types';
import { PortalioSelectAllListItem } from './portalio-select-all-list-item';

const PAGE_SIZE = 20;

export const PortalioProposalsList = ({
  filterText,
  onNavigateAway,
  onProjectsSelected,
  showSelectAll,
  initialSelectedIds,
}: ProjectsListProps<PortalioProposalItem>) => {
  const { navigate } = useNavigateRoute();
  const theme = useTheme();
  const { portalioAuthDetails } = useGetPortalioAuthDetails();
  const [currentPage, setCurrentPage] = useState(1);
  const [fetchMoreLoading, setFetchMoreLoading] = useState(false);
  const {
    proposals,
    loading,
    fetchMore,
    total = 0,
  } = useGetPortalioProposals({
    pageSize: PAGE_SIZE,
    pageNumber: 1,
    searchValue: filterText,
  });
  const [selectedAll, setSelectedAll] = useState<boolean>(false);
  const [selectedProposals, setSelectedProposals] = useState<PortalioProposalItem[]>([]);

  const onProposalClickHandler = useCallback(
    (proposal: PortalioProposalItem) => () => {
      const nextValue = selectedProposals.some(({ id }) => id === proposal.id)
        ? selectedProposals.filter((p) => p.id !== proposal.id)
        : selectedProposals.concat(proposal);
      setSelectedProposals(nextValue);
      onProjectsSelected?.(nextValue);
    },
    [onProjectsSelected, selectedProposals],
  );

  const onLoadMoreHandler = useCallback(async () => {
    const nextPage = currentPage + 1;
    setFetchMoreLoading(true);
    await fetchMore({
      variables: {
        params: {
          pageSize: PAGE_SIZE,
          searchValue: filterText,
          pageNumber: nextPage,
        },
      },
    });
    setFetchMoreLoading(false);
    setCurrentPage(nextPage);
  }, [currentPage, fetchMore, filterText]);

  const onSelectAll = useCallback(() => {
    if (!proposals) {
      return;
    }
    const nextState = !selectedAll;
    const nextProposals = nextState ? proposals : [];
    setSelectedAll(nextState);
    setSelectedProposals(nextProposals);
    onProjectsSelected?.(nextProposals);
  }, [onProjectsSelected, proposals, selectedAll]);

  useEffect(() => {
    if (filterText?.length) {
      setCurrentPage(1);
    }
  }, [filterText]);

  useEffect(() => {
    setSelectedProposals((proposals || [])
      .filter(({ id }) => initialSelectedIds?.includes(id)));
    setSelectedAll((initialSelectedIds?.length ?? 0) > 0 && initialSelectedIds?.length === proposals?.length);
  // Intentionally incomplete set of dependencies to properly handle initialSelected values on first render only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposals]);

  const listItems = useMemo(
    () =>
      (proposals || [])
        .filter(
          (p) =>
            p.name.toLowerCase().includes(filterText ?? '') ||
            p.proposalNumber.toString().includes(filterText ?? ''),
        )
        .map((p) => {
          const labelId = `checkbox-list-label-${p.id}`;
          const selected = selectedProposals.some(({ id }) => id === p.id);
          return (
            <ListItem key={p.id} dense disableGutters disablePadding>
              <ListItemButton
                role={undefined}
                onClick={onProposalClickHandler(p)}
                disableRipple
                sx={{
                  backgroundColor: selected ? theme.palette.action.hover : undefined,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: theme.spacing(4),
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={selected}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                    slotProps={{
                      input: {
                        'aria-labelledby': labelId,
                      },
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  id={labelId}
                  primary={p.name}
                  secondary={`#${p.proposalNumber} - ${p.customer?.firstName} ${p.customer?.lastName ?? ''} - ${dayjs(p.lastModifiedDate ?? p.createdDate).format(DISPLAY_DATE_TIME_FORMAT)}`}
                />
              </ListItemButton>
            </ListItem>
          );
        }),
    [filterText, onProposalClickHandler, proposals, selectedProposals, theme],
  );

  if (loading) {
    return (
      <List>
        <ListItem>
          <Skeleton animation="wave" variant="text" width="60%" />
        </ListItem>
        <ListItem>
          <Skeleton animation="wave" variant="text" width="60%" />
        </ListItem>
        <ListItem>
          <Skeleton animation="wave" variant="text" width="60%" />
        </ListItem>
      </List>
    );
  }

  if (!portalioAuthDetails?.id) {
    return (
      <Alert
        severity="warning"
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              onNavigateAway?.();
              navigate(portalPaths.settings.integrations());
            }}
          >
            Open Integrations page
          </Button>
        }
      >
        <Typography>
          You&#39;re not connected to Portal.io. Go to Integrations Settings page to configure
          integration.
        </Typography>
      </Alert>
    );
  }

  return (
    <List dense>
      <PortalioSelectAllListItem
        showSelectAll={showSelectAll}
        onSelectAll={onSelectAll}
        selectedAll={selectedAll}
        proposalsLength={proposals?.length}
        selectedProposalsCount={selectedProposals.length}
      />
      {listItems}
      {listItems.length < total && listItems.length > 0 ? (
        <ListItem dense disableGutters disablePadding>
          <ListItemButton
            sx={{
              paddingY: theme.spacing(),
              justifyContent: 'center',
            }}
            onClick={onLoadMoreHandler}
            disableRipple
            disabled={fetchMoreLoading}
          >
            {fetchMoreLoading ? (
              <ListItemIcon>
                <CircularProgress size={20} />
              </ListItemIcon>
            ) : (
              <Typography variant="body2">Load More...</Typography>
            )}
          </ListItemButton>
        </ListItem>
      ) : null}
    </List>
  );
};
